import * as CustomTranslate from '../../services/translate/customTranslateService';
import * as bookApi from '../../services/api/bookApi';
import * as gradebookApi from '../../services/api/gradebookApi';
import * as activitiesApi from '../../services/api/activitiesApi';
import { Cmi5 } from '../../services/cmi5/cmi5';
import * as activityService from '../../services/domain/activityService';
import { REDUCER } from '../../reducers/reducersConst';
import * as Promise from 'bluebird';
import { GRADEBOOK_SCOPE } from '../../const';
import * as classroomApi from '../../services/api/classroomApi';
import { isUserInstructor } from '../../services/jwt/jwtService';
import * as messageAlert from '../messageAlertAction';
import { I18n } from 'react-redux-i18n-lite';
import { getUserIds } from '../taskActions';
import * as taskService from '../../services/domain/taskService';
import * as backofficeApi from '../../services/api/backofficeApi';
import debug from 'debug';

/**
 * Sets grades on the store
 * @param grades
 * @returns {{type: string, grades: {}}}
 */
export let setGrades = (grades = null) => {
  return {
    type: REDUCER.GRADEBOOK.SET_GRADES,
    grades
  };
};

/**
 * Sets the books array on the store
 * @param books
 * @returns {{type: string, books: []}}
 */
export let setBooksData = (books = null) => {
  return {
    type: REDUCER.GRADEBOOK.SET_BOOKS_DATA,
    books
  };
};

export let setGradebookScope = (gradebookScope = GRADEBOOK_SCOPE.ACTIVITIES) => {
  return {
    type: REDUCER.GRADEBOOK.SET_GRADEBOOK_SCOPE,
    gradebookScope
  };
};

/**
 * Sets the units array on the store
 * @param units
 * @returns {{type: string, units: []}}
 */
export let setUnitsData = (units = null) => {
  return {
    type: REDUCER.GRADEBOOK.SET_UNITS_DATA,
    units
  };
};

/**
 * Sets the lessons array on the store
 * @param lessons
 * @returns {{type: string, lessons: []}}
 */
export let setLessonsData = (lessons = null) => {
  return {
    type: REDUCER.GRADEBOOK.SET_LESSONS_DATA,
    lessons
  };
};

/**
 * Sets the activities array on the store
 * @param activities
 * @returns {{type: string, activities: []}}
 */
export let setActivitiesData = (activities = null) => {
  return {
    type: REDUCER.GRADEBOOK.SET_ACTIVITIES_DATA,
    activities
  };
};

/**
 * Sets the tasks array on the store
 * @param tasks
 * @returns {{type: string, tasks: []}}
 */
export let setTasksData = (tasks = null) => {
  return {
    type: REDUCER.GRADEBOOK.SET_TASKS_DATA,
    tasks
  };
};

/**
 * Sets the tasks filter params on the store
 * @param filterParams
 * @returns {{type: string, filterParams: []}}
 */
export let setTasksFilterParams = (tasksFilterParams = {}) => {
  return {
    type: REDUCER.GRADEBOOK.SET_TASKS_FILTER_PARAMS,
    tasksFilterParams
  };
};

/**
 * Sets the selected book on the store
 * @param selectedBook
 * @returns {{type: string, selectedBook: {}}}
 */
export let setSelectedBook = (selectedBook = {}) => {
  return {
    type: REDUCER.GRADEBOOK.SET_SELECTED_BOOK,
    selectedBook
  };
};

/**
 * Sets the selected lesson on the store
 * @param selectedLesson
 * @returns {{type: string, selectedLesson: {}}}
 */
export let setSelectedLesson = (selectedLesson = {}) => {
  return {
    type: REDUCER.GRADEBOOK.SET_SELECTED_LESSON,
    selectedLesson
  };
};

/**
 * Sets the selected unit on the store
 * @param selectedUnit
 * @returns {{type: string, selectedUnit: {}}}
 */
export let setSelectedUnit = (selectedUnit = {}) => {
  return {
    type: REDUCER.GRADEBOOK.SET_SELECTED_UNIT,
    selectedUnit
  };
};

/**
 * Set the bookNames
 * @param books
 * @param language
 * @returns books
 */
let updateItemsNames = (list, language) => {
  for (let i = 0; list && i < list.length; i++) {
    if (list[i].name) {
      if (typeof list[i].name === 'string') {
        list[i].name = JSON.parse(list[i].name);
      }
      list[i].name = CustomTranslate.translateObject(list[i].name, language);
    }
  }
  return list;
};

/**
 * Action to request the books form a certain classroom
 * @param classroomId
 * @returns {function(*, *)}
 */
export let getBooksData = (classroomId = '') => {
  return (dispatch, getState) => {
    let store = getState();
    let language = store.account.user.language;
    if (classroomId) {
      return bookApi.getBooks(classroomId, true).then(res => {
        let books = updateItemsNames(res.data, language);
        dispatch(setBooksData(books));
        return res.data;
      });
    }
  };
};

/**
 * Actions to request the book structure in the context of a certain classroom or section
 * @param classroomId Identifier for classroom
 * @param filterParams
 * @returns {function(*, *)}
 */
export let changeTasksFilterParams = (classroomId, filterParams) => {
  return (dispatch, getState) => {
    dispatch(setTasksFilterParams(filterParams));
    dispatch(loadTasks(classroomId, filterParams));
  };
};

/**
 * Actions to request the book structure in the context of a certain classroom or section
 * @param classroomId Identifier for classroom
 * @param filterParams
 * @returns {function(*, *)}
 */
export let loadTasks = (classroomId, filterParams) => {
  return (dispatch, getState) => {
    // TODO: This is a temporary solution to get all the tasks in a classroom
    const perPage = 999;
    const state = getState();
    const userId = state.account.user._id;
    classroomApi
      .getPaginatedAssignments(classroomId, userId, 1, filterParams, perPage)
      .then(res => {
        const paginatedData = res.data;
        const totalPages = paginatedData.pagination.totalPages;
        if (isUserInstructor(getState())) {
          return Promise.map(paginatedData.tasks, task => {
            let students = getUserIds(task.studentAttempts);
            let noLessonActivities = taskService.getActivitiesWithoutLessons(task.activities);
            return backofficeApi
              .getActivityTrackingSummary(task.parentSection, noLessonActivities, task.dueDate, students)
              .then(res => {
                task.resultSummary = taskService.getTaskResultsForInstructor(res.data);
                task.errorGettingResults = false;
              })
              .catch(err => {
                debug.error('[getTasksForInstructor] error getting results %s', err);
                task.errorGettingResults = true;
              });
          }).then(() => {
            if (paginatedData.tasks.length) {
              const promises = [];
              paginatedData.tasks.map(task => {
                task.activitiesState = [];
                task.activitiesStateNotracking = [];
                task.bookActivities.map(bookAct => {
                  if (bookAct.activityType === 'https://w3id.org/xapi/smart/activity/unit') {
                    promises.push(
                      backofficeApi.getActivitisStateInstructor(task.parentSection, bookAct.activityId).then(res => {
                        res.data.map(act => {
                          if (task.activities.find(activit => activit.activityId === act.activityId)) {
                            task.activitiesState.push(act);
                          }
                        });
                      })
                    );
                  }
                });
              });
              return Promise.all(promises).then(() => {
                dispatch(setTasksData(paginatedData.tasks));
              });
            } else {
              dispatch(setTasksData([]));
            }
          });
        }
      })
      .catch(error => {
        debug.error('[getAssignments] error getting assignments %s', error);
        dispatch(messageAlert.error(I18n.t('tasks.tasksErrors')));
      });
    dispatch(setSelectedUnit(null));
    dispatch(setSelectedLesson(null));
    dispatch(setLessonsData([]));
    dispatch(setActivitiesData([]));
    dispatch(setSelectedBook(null));
    dispatch(setUnitsData([]));
    dispatch(setGradebookScope(GRADEBOOK_SCOPE.TASKS));
    return Promise.resolve();
  };
};

/**
 * Actions to request the book structure in the context of a certain classroom or section
 * @param classroomId Identifier for classroom
 * @param tasksPage
 * @param filterParams
 * @returns {function(*, *)}
 */
export let nextPageTasks = (classroomId, tasksPage, filterParams) => {
  return (dispatch, getState) => {
    const perPage = 10;
    const state = getState();
    const userId = state.account.user._id;
    const actualTasks = state.gradebook.tasks;
    classroomApi
      .getPaginatedAssignments(classroomId, userId, tasksPage, filterParams, perPage)
      .then(res => {
        const paginatedData = res.data;
        const totalPages = paginatedData.pagination.totalPages;
        if (isUserInstructor(getState())) {
          return Promise.map(paginatedData.tasks, task => {
            let students = getUserIds(task.studentAttempts);
            let noLessonActivities = taskService.getActivitiesWithoutLessons(task.activities);
            return backofficeApi
              .getActivityTrackingSummary(task.parentSection, noLessonActivities, task.dueDate, students)
              .then(res => {
                task.resultSummary = taskService.getTaskResultsForInstructor(res.data);
                task.errorGettingResults = false;
              })
              .catch(err => {
                debug.error('[getTasksForInstructor] error getting results %s', err);
                task.errorGettingResults = true;
              });
          }).then(() => {
            if (paginatedData.tasks.length) {
              const promises = [];
              paginatedData.tasks.map(task => {
                task.activitiesState = [];
                task.activitiesStateNotracking = [];
                task.bookActivities.map(bookAct => {
                  if (bookAct.activityType === 'https://w3id.org/xapi/smart/activity/unit') {
                    promises.push(
                      backofficeApi.getActivitisStateInstructor(task.parentSection, bookAct.activityId).then(res => {
                        res.data.map(act => {
                          if (task.activities.find(activit => activit.activityId === act.activityId)) {
                            task.activitiesState.push(act);
                          }
                        });
                      })
                    );
                  }
                });
              });
              return Promise.all(promises).then(() => {
                dispatch(setTasksData([...actualTasks, ...paginatedData.tasks]));
              });
            } else {
              dispatch(setTasksData([]));
            }
          });
        }
      })
      .catch(error => {
        debug.error('[getAssignments] error getting assignments %s', error);
        dispatch(messageAlert.error(I18n.t('tasks.tasksErrors')));
      });
    dispatch(setSelectedUnit(null));
    dispatch(setSelectedLesson(null));
    dispatch(setLessonsData([]));
    dispatch(setActivitiesData([]));
    dispatch(setSelectedBook(null));
    dispatch(setUnitsData([]));
    dispatch(setGradebookScope(GRADEBOOK_SCOPE.TASKS));
    return Promise.resolve();
  };
};

/**
 * Actions to request the book structure in the context of a certain classroom or section
 * @param classroomId
 * @param bookId
 * @returns {function(*, *)}
 */
export let setBookData = (classroomId = '', book, sectionId = null) => {
  return (dispatch, getState) => {
    if (book) {
      book.sectionId = sectionId;
      let store = getState();
      dispatch(setGrades());
      dispatch(setSelectedBook(book));
      gradebookApi
        .getBookUnitsGrades(book)
        .then(async grades => {
          dispatch(setGrades(grades.data));
          let units = await getBookUnits(book.activityId, sectionId, store);
          dispatch(setUnitsData(units));
        })
        .catch(err => {
          dispatch(setSelectedBook(null));
          dispatch(setGrades(null));
        });
    } else {
      dispatch(setSelectedBook(null));
      dispatch(setUnitsData([]));
    }
    dispatch(setSelectedUnit(null));
    dispatch(setSelectedLesson(null));
    dispatch(setLessonsData([]));
    dispatch(setActivitiesData([]));
    return Promise.resolve();
  };
};

export let setUnitData = (unit, sectionId = null, book, idClassroom) => {
  return (dispatch, getState) => {
    if (unit) {
      unit.sectionId = sectionId;
      dispatch(setGrades());
      gradebookApi
        .getUnitLessonsGrades(unit)
        .then(async grades => {
          const store = getState();
          const language = store.account.user.language;
          const unitId = unit.id || unit.activityId;
          const lessons = await getChildrensByActivityId(unitId, sectionId, language);
          dispatch(setGrades(grades.data));
          dispatch(setSelectedUnit(unit));
          dispatch(setSelectedLesson(null));
          dispatch(setLessonsData(lessons));
          dispatch(setActivitiesData([]));
        })
        .catch(err => {
          dispatch(setSelectedUnit(null));
          dispatch(setSelectedLesson(null));
          dispatch(setLessonsData([]));
          dispatch(setActivitiesData([]));
          dispatch(setGrades(null));
        });
    } else {
      dispatch(setSelectedUnit(null));
      dispatch(setSelectedLesson(null));
      dispatch(setLessonsData([]));
      dispatch(setActivitiesData([]));
      dispatch(setBookData(idClassroom, book, sectionId));
    }
  };
};

export let setLessonData = (lesson, sectionId = null, unit, book) => {
  return async (dispatch, getState) => {
    if (lesson) {
      lesson.sectionId = sectionId;
      dispatch(setGrades());
      gradebookApi
        .getLessonActivitiesGrades(lesson)
        .then(async grades => {
          const store = getState();
          const language = store.account.user.language;
          const lessonId = lesson.id || lesson.activityId;
          dispatch(setGrades(grades.data));
          const activities = await getChildrensByActivityId(lessonId, sectionId, language);
          dispatch(setSelectedLesson(lesson));
          dispatch(setActivitiesData(activities));
        })
        .catch(err => {
          dispatch(setSelectedLesson(null));
          dispatch(setActivitiesData([]));
          dispatch(setGrades(null));
        });
    } else {
      dispatch(setSelectedLesson(null));
      dispatch(setActivitiesData([]));
      dispatch(setUnitData(unit, sectionId, book));
    }
  };
};

export let setData = (sectionId, book, unit, lesson) => {
  return async (dispatch, getState) => {
    if (book) {
      let store = getState();
      dispatch(setGrades());
      dispatch(setSelectedBook(book));
      dispatch(setSelectedUnit(unit));
      dispatch(setSelectedLesson(lesson));
      let units = await getBookUnits(book.activityId, sectionId, store);
      dispatch(setUnitsData(units));

      if (lesson) {
        lesson.sectionId = sectionId;
        gradebookApi
          .getLessonActivitiesGrades(lesson)
          .then(async grades => {
            const store = getState();
            const language = store.account.user.language;
            const unitId = unit.id || unit.activityId;
            const lessonId = lesson.id || lesson.activityId;
            const activities = await getChildrensByActivityId(lessonId, sectionId, language);
            dispatch(setGrades(grades.data));
            dispatch(setActivitiesData(activities));
            const lessons = await getChildrensByActivityId(unitId, sectionId, language);
            dispatch(setLessonsData(lessons));
          })
          .catch(err => {
            dispatch(setSelectedLesson(null));
            dispatch(setActivitiesData([]));
            dispatch(setGrades(null));
          });
      } else if (unit) {
        unit.sectionId = sectionId;
        gradebookApi
          .getUnitLessonsGrades(unit)
          .then(async grades => {
            const store = getState();
            const language = store.account.user.language;
            const unitId = unit.id || unit.activityId;
            const lessons = await getChildrensByActivityId(unitId, sectionId, language);
            dispatch(setGrades(grades.data));
            dispatch(setSelectedLesson(null));
            dispatch(setActivitiesData([]));
            dispatch(setLessonsData(lessons));
          })
          .catch(err => {
            dispatch(setSelectedUnit(null));
            dispatch(setSelectedLesson(null));
            dispatch(setLessonsData([]));
            dispatch(setActivitiesData([]));
            dispatch(setGrades(null));
          });
      } else {
        book.sectionId = sectionId;
        gradebookApi
          .getBookUnitsGrades(book)
          .then(async grades => {
            dispatch(setSelectedUnit(null));
            dispatch(setSelectedLesson(null));
            dispatch(setGrades(grades.data));
          })
          .catch(err => {
            dispatch(setSelectedBook(null));
            dispatch(setGrades(null));
          });
      }
    } else {
      dispatch(setSelectedBook(null));
      dispatch(setUnitsData([]));
    }
    return Promise.resolve();
  };
};

function getBookUnits(activityId, sectionId, store) {
  let language = store.account.user.language;
  let user = store.account.user;
  if (activityId && user) {
    return activitiesApi.getContentByActivityId(sectionId, activityId).then(res => {
      const content = res.data;
      const unitsArray = content.auOrBlock;
      let book = content.course;

      book.name = CustomTranslate.translateObject(book.title, language);
      book.sectionId = sectionId;
      let units = unitsArray.filter(unit => {
        unit.name = CustomTranslate.translateObject(unit.title, language);
        unit.sectionId = sectionId;
        return unit;
      });

      return units;
    });
  }
}

function getChildrensByActivityId(activityId, sectionId, language) {
  return activitiesApi.getContentAndChildrenByActivityId(sectionId, activityId).then(res => {
    let cmi5 = new Cmi5(res.data);
    const lessonsArray = cmi5.getChildren();
    let children = lessonsArray.filter(item => {
      item.cmi5.name = CustomTranslate.translateObject(item.cmi5.title, language);
      item.cmi5.hasChildren = item.getHasChildrenByActivityTypes(activityService.ASSESSABLE_TYPES);
      return item;
    });
    return children;
  });
}
